import React, { useState, useRef, useEffect } from 'react';
import { ShieldIcon, KeyIcon } from '../common/Icons';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: Record<string, unknown>;
  recovery_codes_remaining?: number;
}

interface MFAChallengeModalProps {
  mfaToken: string;
  mfaMethods: string[];
  onSuccess: (tokenResponse: TokenResponse) => void;
  onCancel: () => void;
}

const MFAChallengeModal: React.FC<MFAChallengeModalProps> = ({
  mfaToken,
  mfaMethods,
  onSuccess,
  onCancel,
}) => {
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lowCodesWarning, setLowCodesWarning] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    if (mode === 'totp' && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [mode]);

  const handleDigitChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);
    setError('');

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newDigits.every(d => d)) {
      submitTotp(newDigits.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newDigits = pasted.split('');
      setDigits(newDigits);
      inputRefs.current[5]?.focus();
      submitTotp(pasted);
    }
  };

  const submitTotp = async (code: string) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('/api/auth/mfa/challenge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_token: mfaToken, code }),
      });

      if (response.ok) {
        const data = await response.json();
        onSuccess(data);
      } else {
        const err = await response.json().catch(() => ({ detail: 'Verification failed' }));
        setError(err.detail || 'Invalid code. Please try again.');
        setDigits(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const submitRecovery = async () => {
    if (!recoveryCode.trim()) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('/api/auth/mfa/recovery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_token: mfaToken, recovery_code: recoveryCode }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.recovery_codes_remaining !== undefined && data.recovery_codes_remaining < 3) {
          setLowCodesWarning(data.recovery_codes_remaining);
        }
        onSuccess(data);
      } else {
        const err = await response.json().catch(() => ({ detail: 'Invalid recovery code' }));
        setError(err.detail || 'Invalid recovery code.');
        setRecoveryCode('');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mfa-modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
    }}>
      <div className="glass-panel" style={{
        width: '100%',
        maxWidth: '420px',
        padding: '32px',
        borderRadius: '16px',
        margin: '16px',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'var(--accent-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <ShieldIcon size={24} />
          </div>
          <h2 style={{
            fontSize: 'var(--text-xl)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            margin: 0,
          }}>
            Two-Factor Authentication
          </h2>
          <p style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
            marginTop: '8px',
          }}>
            {mode === 'totp'
              ? 'Enter the 6-digit code from your authenticator app'
              : 'Enter one of your recovery codes'
            }
          </p>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '10px 14px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#ef4444',
            fontSize: 'var(--text-sm)',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            {error}
          </div>
        )}

        {/* Low codes warning */}
        {lowCodesWarning !== null && (
          <div style={{
            padding: '10px 14px',
            borderRadius: '8px',
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            color: '#f59e0b',
            fontSize: 'var(--text-sm)',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            Warning: Only {lowCodesWarning} recovery code{lowCodesWarning !== 1 ? 's' : ''} remaining.
          </div>
        )}

        {/* TOTP Input */}
        {mode === 'totp' ? (
          <div>
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              marginBottom: '24px',
            }} onPaste={handlePaste}>
              {digits.map((digit, i) => (
                <React.Fragment key={i}>
                  {i === 3 && (
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--text-secondary)',
                      fontSize: '20px',
                    }}>-</span>
                  )}
                  <input
                    ref={el => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleDigitChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    disabled={isLoading}
                    autoComplete="one-time-code"
                    style={{
                      width: '48px',
                      height: '56px',
                      textAlign: 'center',
                      fontSize: '24px',
                      fontWeight: 600,
                      borderRadius: '10px',
                      border: '1px solid var(--border-primary)',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={e => { e.target.style.borderColor = 'var(--accent-primary)'; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border-primary)'; }}
                  />
                </React.Fragment>
              ))}
            </div>

            <button
              className="btn btn-primary btn-lg"
              onClick={() => submitTotp(digits.join(''))}
              disabled={isLoading || digits.some(d => !d)}
              style={{ width: '100%' }}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        ) : (
          /* Recovery Code Input */
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div className="input-wrapper" style={{ position: 'relative' }}>
                <KeyIcon size={18} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-secondary)',
                }} />
                <input
                  type="text"
                  className="input input-lg"
                  placeholder="XXXX-XXXX"
                  value={recoveryCode}
                  onChange={e => { setRecoveryCode(e.target.value); setError(''); }}
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    paddingLeft: '40px',
                    textAlign: 'center',
                    fontFamily: 'monospace',
                    fontSize: '18px',
                    letterSpacing: '2px',
                  }}
                  onKeyDown={e => { if (e.key === 'Enter') submitRecovery(); }}
                  autoFocus
                />
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg"
              onClick={submitRecovery}
              disabled={isLoading || !recoveryCode.trim()}
              style={{ width: '100%' }}
            >
              {isLoading ? 'Verifying...' : 'Use Recovery Code'}
            </button>
          </div>
        )}

        {/* Toggle Mode */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          margin: '20px 0 16px',
        }}>
          <div style={{
            flex: 1,
            height: '1px',
            background: 'var(--border-primary)',
          }} />
          <span style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}>or</span>
          <div style={{
            flex: 1,
            height: '1px',
            background: 'var(--border-primary)',
          }} />
        </div>

        {mfaMethods.includes('recovery') && mode === 'totp' && (
          <button
            onClick={() => { setMode('recovery'); setError(''); setDigits(['', '', '', '', '', '']); }}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              color: 'var(--accent-primary)',
              cursor: 'pointer',
              fontSize: 'var(--text-sm)',
              padding: '8px',
            }}
          >
            Use a recovery code instead
          </button>
        )}

        {mode === 'recovery' && (
          <button
            onClick={() => { setMode('totp'); setError(''); setRecoveryCode(''); }}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              color: 'var(--accent-primary)',
              cursor: 'pointer',
              fontSize: 'var(--text-sm)',
              padding: '8px',
            }}
          >
            Use authenticator app instead
          </button>
        )}

        {/* Cancel */}
        <button
          onClick={onCancel}
          style={{
            width: '100%',
            background: 'none',
            border: 'none',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            fontSize: 'var(--text-sm)',
            padding: '8px',
            marginTop: '4px',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default MFAChallengeModal;
